import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { UserPlus, MoreHorizontal, Search } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsTable } from "@/components/v2/settings-table";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCurrentUser } from "@/hooks/use-current-user";
import {
  useAccountMembers,
  useAccountRemoveUser,
  useAccountResendInvite,
  useAccountResetPassword,
  useAccountSuspendUser,
  useAccountUnsuspendUser,
  useAccountDeleteUser,
} from "@/hooks/use-account-users";
import type { AccountUserOnAccount } from "@adva/types";
import { formatRole, formatRelativeDate } from "@/lib/format-utils";
import { InviteMemberSheet } from "@/components/settings/invite-member-sheet";
import { EditRoleSheet } from "@/components/settings/edit-role-sheet";

// ─── Member Row Actions ───────────────────────────────────────────────────────

interface PendingConfirm {
  title: string;
  description: string;
  onConfirm: () => void;
}

interface MemberRowActionsProps {
  member: AccountUserOnAccount;
  currentEmail: string | undefined;
  onEditRole: (member: AccountUserOnAccount) => void;
}

function MemberRowActions({
  member,
  currentEmail,
  onEditRole,
}: MemberRowActionsProps) {
  const resendInvite = useAccountResendInvite();
  const resetPassword = useAccountResetPassword();
  const suspend = useAccountSuspendUser();
  const unsuspend = useAccountUnsuspendUser();
  const remove = useAccountRemoveUser();
  const del = useAccountDeleteUser();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const isSelf = member.email === currentEmail;
  const isPending = member.status === "invited";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Member actions"
            className="h-7 w-7 p-0"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48 bg-[color:var(--color-surface)] shadow-md border-[color:var(--color-border)]"
        >
          {isPending && (
            <DropdownMenuItem onClick={() => resendInvite.mutate(member.id)}>
              Resend invite
            </DropdownMenuItem>
          )}
          {!isPending && (
            <DropdownMenuItem onClick={() => resetPassword.mutate(member.id)}>
              Reset password
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onEditRole(member)}>
            Edit role
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {!isSelf && member.status === "active" && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  title: "Suspend member?",
                  description: `Suspend ${member.email}? They will lose access to this account.`,
                  onConfirm: () => suspend.mutate(member.id),
                })
              }
            >
              Suspend
            </DropdownMenuItem>
          )}
          {!isSelf && member.status === "inactive" && (
            <DropdownMenuItem onClick={() => unsuspend.mutate(member.id)}>
              Unsuspend
            </DropdownMenuItem>
          )}
          {!isSelf && (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() =>
                setPending({
                  title: "Remove member?",
                  description: `Remove ${member.email} from your account? They will lose all access.`,
                  onConfirm: () => remove.mutate(member.id),
                })
              }
            >
              Remove from account
            </DropdownMenuItem>
          )}
          {!isSelf && (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() =>
                setPending({
                  title: "Delete user?",
                  description: `Permanently delete ${member.email}? This cannot be undone.`,
                  onConfirm: () => del.mutate(member.id),
                })
              }
            >
              Delete user
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending?.title ?? ""}
        description={pending?.description ?? ""}
        onConfirm={() => {
          pending?.onConfirm();
          setPending(null);
        }}
      />
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function V2SettingsMembersPage() {
  const currentUser = useCurrentUser();
  const { data, isLoading } = useAccountMembers();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMember, setEditMember] = useState<AccountUserOnAccount | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const members = useMemo(() => {
    const allMembers = data?.users ?? [];
    if (!searchQuery.trim()) return allMembers;
    const q = searchQuery.toLowerCase();
    return allMembers.filter(
      (m) =>
        m.email.toLowerCase().includes(q) ||
        m.first_name?.toLowerCase().includes(q) ||
        m.last_name?.toLowerCase().includes(q),
    );
  }, [data?.users, searchQuery]);

  return (
    <SettingsPage
      breadcrumbs={[
        { label: "Settings" },
        { label: "Administration" },
        { label: "Members" },
      ]}
      title="Members"
      subtitle="Manage who has access to your account."
      actions={
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="h-4 w-4 mr-1.5" />
          Invite Member
        </Button>
      }
    >
      <div className="mb-4">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--color-text-md)]" />
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-8"
          />
        </div>
      </div>

      <SettingsTable
        colSpan={6}
        isLoading={isLoading}
        isEmpty={!isLoading && members.length === 0}
        emptyMessage="No members yet. Invite a member above."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Name
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Role
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Status
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden md:table-cell">
              Businesses
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden lg:table-cell">
              Last Sign-in
            </TableHead>
            <TableHead className="w-12 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => {
            const name = [member.first_name, member.last_name]
              .filter(Boolean)
              .join(" ");
            return (
              <TableRow
                key={member.id}
                className="h-9 border-b border-[color:var(--color-border)] last:border-0"
              >
                <TableCell className="px-4 py-1.5">
                  <Link
                    to={`/settings/members/${member.id}`}
                    className="block group focus:outline-none"
                  >
                    <p className="typo-body font-medium text-[color:var(--color-text-hi)] group-hover:underline">
                      {name || member.email}
                    </p>
                    {name && (
                      <p className="typo-body text-[color:var(--color-text-md)]">
                        {member.email}
                      </p>
                    )}
                  </Link>
                </TableCell>
                <TableCell className="px-4 py-1.5">
                  <Badge variant="secondary">
                    {formatRole(member.account_role)}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-1.5">
                  <Badge
                    variant={member.status === "active" ? "default" : "outline"}
                  >
                    {member.status}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-1.5 hidden md:table-cell typo-body text-[color:var(--color-text-md)]">
                  {member.all_businesses_access ? (
                    <span className="italic text-[color:var(--color-text-md)]">
                      All businesses
                    </span>
                  ) : (
                    member.businesses
                      .map((b) => b.business_name ?? b.business_id.slice(0, 8))
                      .join(", ") || "—"
                  )}
                </TableCell>
                <TableCell className="px-4 py-1.5 hidden lg:table-cell typo-body text-[color:var(--color-text-md)]">
                  {formatRelativeDate(member.last_sign_in_at)}
                </TableCell>
                <TableCell className="px-4 py-1.5 text-right">
                  <MemberRowActions
                    member={member}
                    currentEmail={currentUser?.email}
                    onEditRole={setEditMember}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </SettingsTable>

      <InviteMemberSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        accountId={currentUser?.accountId ?? ""}
      />

      <EditRoleSheet member={editMember} onClose={() => setEditMember(null)} />
    </SettingsPage>
  );
}
